import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import { getUnixTime, startOfDay, endOfDay, subDays, fromUnixTime, format } from 'date-fns';
import {
  Spinner,
  DateRangePicker,
  Button,
  Popover,
  PopoverButton,
  StyledPopoverPanel,
  PopoverItem,
} from '@crazyegginc/hatch';

import { ReactComponent as DownloadIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-download.svg';

import { DashboardPage } from '/src/components/Page';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { DashboardPaywall } from '../../_global/paywalls/DashboardPaywall';
import { SitePickerBar } from '/src/components/site-picker/index';
import { AddSiteModal } from '/src/components/modals/AddSiteModal';
import { NoMatchesWall } from '../../_global/paywalls/NoMatchesWall';
import { NotInstalledWall } from '../walls/NotInstalled';
import { columnOrderAtom, getDefaultOrder, lastValidOrderAtom, searchAtom } from '../components/table';
import { DetailView } from './detail';
import { ListView } from './list';

import {
  webAnalyticsOverTimeQuery,
  webAnalyticsQuery,
  webAnalyticsConversionsOverTimeQuery,
  webAnalyticsDimensionQuery,
} from '../queries';
import {
  useAuthContext,
  useSite,
  useQueryParams,
  usePermissions,
  useNotifications,
  useHasFeatureFlag,
} from '/src/hooks';

import { FEATURES, SPECIAL_DATE_RANGES, TIMEZONES } from '/src/features/_global/constants';
import { WEB_ANALYTIC_METRICS, VALID_DIMENSION_VALUES, OTHERS } from '../constants';

import { humanizeDateRange, parseDateRange, parsePredefinedRange, recentlyEnabled } from '../utils';
import { toUtcStart, toUtcEnd } from '/src/utils/date';
import { getFeatureName } from '/src/features/_global/utils';
import { toAtom } from '/src/utils/string';
import { ensureValidSearch } from '/src/utils/url';
import { scrollToLater } from '/src/utils/scroll';

import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { OptInWall } from '../walls/OptIn';
import { selectedGoalAtom } from '../components/goal-selector';
import { AskOwnerWall } from '../walls/AskOwner';

const MAX_DAYS = 365;

// eslint-disable-next-line no-restricted-syntax
export default function WebAnalyticsDashboard() {
  const { sites, selectedSite, loadingSites, reloadSites } = useSite(); // select the default site
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.WEB_ANALYTICS);

  useEffect(() => {
    if (selectedSite) {
      // set default params
      const newParams = new URLSearchParams(window.location.search);
      let mutated = false;

      if (!newParams.has('date_range')) {
        newParams.set(
          'date_range',
          recentlyEnabled(selectedSite.webAnalyticsEnabledAt)
            ? SPECIAL_DATE_RANGES.LAST_7_DAYS
            : SPECIAL_DATE_RANGES.LAST_30_DAYS,
        );
        mutated = true;
      }
      if (!newParams.has('metric')) {
        newParams.set('metric', WEB_ANALYTIC_METRICS.UNIQUE_VISITORS);
        mutated = true;
      }

      if (mutated) {
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [setSearchParams, selectedSite]);

  const params = Object.fromEntries(searchParams.entries());

  const hasValidParams = !!params.site && !!params.date_range && !!params.metric;
  const firstSite = !loadingSites && !sites.length;

  if (!canViewDashboard) {
    return <DashboardPaywall reason={reason} feature={FEATURES.WEB_ANALYTICS} />;
  }

  const siteLoading = Boolean(!selectedSite && loadingSites);

  return (
    <DashboardPage>
      <SEO title="Web Analytics" />
      <DashHeader titleComponent={getFeatureName(FEATURES.WEB_ANALYTICS)} />

      {firstSite ? (
        <AddSiteModal
          customTitle="Add a site"
          onExtraSteps={() => {
            reloadSites();
          }}
          onCancel={() => navigate('/options', { replace: true })}
        />
      ) : hasValidParams && !siteLoading ? (
        <WebAnalyticsContent />
      ) : (
        <div className="mt-16 flex w-full items-center justify-center">
          <Spinner />
          <div className="ml-2.5">Loading...</div>
        </div>
      )}
    </DashboardPage>
  );
}

function WebAnalyticsContent() {
  const permissions = usePermissions();
  const { sites, loadingSites, selectedSite, selectSite } = useSite();

  const { allowed: canEdit } = permissions.can('edit', FEATURES.WEB_ANALYTICS);

  if (!selectedSite) return null;

  return (
    <>
      <SitePickerBar
        sites={sites}
        loading={loadingSites}
        selectedSite={selectedSite}
        selectSite={selectSite}
        enableAllSites={true}
        pageDisplayName="Web Analytics dashboard"
      />
      <div className="flex flex-col p-10 !pt-5">
        {selectedSite && !selectedSite.installed ? (
          <NotInstalledWall />
        ) : selectedSite && !selectedSite.webAnalyticsEnabled ? (
          canEdit ? (
            <OptInWall />
          ) : (
            <AskOwnerWall />
          )
        ) : (
          <RenderAnalytics />
        )}
      </div>
    </>
  );
}

const DEFAULT_LIMIT = 250;

function parseOrderDir(view, desc) {
  if (!view) return undefined;
  if (desc === true) return 'DESC';
  if (desc === false) return 'ASC';
  return undefined;
}

function parseOrderBy(view, field) {
  if (!view) return undefined;
  return toAtom(field);
}

// Only run queries in this component, we use the ones above
// to ensure the params are set and the site is loaded.
function RenderAnalytics() {
  const permissions = usePermissions();
  const { token } = useAuthContext();
  const location = useLocation();
  const hasOverTime = useHasFeatureFlag('analytics-over-time');
  const canExportJson = useHasFeatureFlag('web-analytics-json-export');
  const canExportCsv = permissions.can('csv', FEATURES.EXPORT).allowed;
  const isDashboard = location.pathname.endsWith('analytics');

  // whether to show the export button
  const canExport = canExportJson || canExportCsv;

  const { selectedSite } = useSite();
  const { get: paramGet, set: paramSet } = useQueryParams();
  const [order, setOrder] = useAtom(columnOrderAtom);
  const [selectedGoal] = useAtom(selectedGoalAtom);
  const [lastValidOrder, setLastValidOrder] = useAtom(lastValidOrderAtom);
  const [search, setSearch] = useAtom(searchAtom);
  const { view } = useParams();
  const navigate = useNavigate();
  const notifications = useNotifications();

  const analyticsEnabledAt = selectedSite?.webAnalyticsEnabledAt
    ? startOfDay(fromUnixTime(selectedSite.webAnalyticsEnabledAt))
    : null;
  const thirtyDaysAgo = startOfDay(subDays(new Date(), 30));

  const pickerLowerBoundary = analyticsEnabledAt
    ? analyticsEnabledAt < thirtyDaysAgo
      ? analyticsEnabledAt
      : thirtyDaysAgo
    : thirtyDaysAgo;

  const pickerUpperBoundary = endOfDay(new Date());

  const numSegments = Number(paramGet('num_segments', '10'));

  useEffect(() => {
    if (!view) return;
    if (!VALID_DIMENSION_VALUES.includes(view)) {
      notifications.error({
        id: 'invalid-dimension',
        content: 'Invalid dimension selected.',
        timeout: 2000,
        skipHoneybadger: true,
      });
      navigate(`/web-analytics${location.search}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const dateRange = paramGet('date_range');

  const parsedDateRange = parseDateRange(dateRange, pickerLowerBoundary, pickerUpperBoundary, MAX_DAYS);

  const [startDate, endDate, predefinedRange] = parsedDateRange;

  useEffect(() => {
    if (startDate && endDate) {
      const [currStart, currEnd] = dateRange.split(',');
      if (Number(currStart) !== startDate || Number(currEnd) !== endDate) {
        paramSet('date_range', `${startDate},${endDate}`);
      }
    }
  }, [paramSet, dateRange, startDate, endDate]);

  const orderColumnForOverTime = parseOrderBy(view, order?.id, false);

  const { data: rawOverTime, isLoading: overTimeLoading } = useQuery({
    ...webAnalyticsOverTimeQuery({
      dimension: view,
      metric: orderColumnForOverTime || lastValidOrder,
      siteId: selectedSite?.id,
      startAt: toUtcStart(startDate),
      endAt: toUtcEnd(endDate),
      presetDate: predefinedRange,
      numSegments: numSegments,
      goalId: selectedGoal?.id,
    }),
    refetchOnMount: 'always',
    enabled: hasOverTime && Boolean(selectedSite) && Boolean(view) && !isDashboard,
  });

  const { data: rawConversions, isLoading: _conversionsLoading } = useQuery({
    ...webAnalyticsConversionsOverTimeQuery({
      goalId: selectedGoal?.id,
      siteId: selectedSite?.id,
      startAt: toUtcStart(startDate),
      endAt: toUtcEnd(endDate),
      presetDate: predefinedRange,
    }),
    refetchOnMount: 'always',
    enabled: hasOverTime && Boolean(selectedGoal?.id) && Boolean(selectedSite?.id) && isDashboard,
  });

  const {
    data: dashData,
    isLoading: dashLoading,
    hasNextPage: dashHasNextPage,
    fetchNextPage: dashFetchNextPage,
  } = useInfiniteQuery({
    ...webAnalyticsQuery({
      view: 'ALL',
      siteId: selectedSite?.id,
      startAt: toUtcStart(startDate),
      endAt: toUtcEnd(endDate),
      presetDate: predefinedRange,
      orderBy: parseOrderBy(view, order?.id),
      orderDir: parseOrderDir(view, order?.desc),
      limit: view ? DEFAULT_LIMIT : 25,
    }),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: isDashboard && Boolean(selectedSite),
  });

  const {
    data: detailData,
    isLoading: detailLoading,
    hasNextPage: detailHasNextPage,
    fetchNextPage: detailFetchNextPage,
  } = useInfiniteQuery({
    ...webAnalyticsDimensionQuery({
      dimension: toAtom(view),
      siteId: selectedSite?.id,
      startAt: toUtcStart(startDate),
      endAt: toUtcEnd(endDate),
      presetDate: predefinedRange,
      searchTerm: search,
      orderBy: parseOrderBy(view, order?.id),
      orderDir: parseOrderDir(view, order?.desc),
      limit: view ? DEFAULT_LIMIT : 25,
    }),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: !isDashboard && Boolean(selectedSite),
  });

  const data = isDashboard ? dashData : detailData;
  const isLoading = isDashboard ? dashLoading : detailLoading;
  const hasNextPage = isDashboard ? dashHasNextPage : detailHasNextPage;
  const fetchNextPage = isDashboard ? dashFetchNextPage : detailFetchNextPage;

  const pageData = useMemo(() => {
    if (!data?.pages) return null;

    if (isDashboard) {
      return data.pages?.[0]?.analytics ?? {};
    }

    let accData = data.pages.reduce((acc, page) => [...acc, ...(page?.analyticsDimension?.dimension?.data ?? [])], []);

    const lastPage = data.pages.at(-1)?.analyticsDimension ?? {};

    return {
      nextCursor: lastPage.nextCursor,
      dimension: {
        data: accData,
        totalCount: lastPage.dimension.totalCount,
      },
    };
  }, [data, isDashboard]);

  const webAnalyticsData = useMemo(() => {
    return {
      ...pageData,
      dimension: {
        ...(pageData?.dimension ?? {}),
      },
    };
  }, [pageData]);

  const overTimeData = useMemo(() => {
    const data = rawOverTime?.analyticsOverTime?.[view]?.overTime ?? [];

    const reordered = data.slice().sort((a, b) => {
      const isAOthers = a.name === OTHERS;
      const isBOthers = b.name === OTHERS;

      if (isAOthers && !isBOthers) return 1;
      if (!isAOthers && isBOthers) return -1;
      if ((isAOthers && isBOthers) || (!isAOthers && !isBOthers)) return 0;
    });

    return reordered;
  }, [rawOverTime?.analyticsOverTime, view]);

  const conversionsData = useMemo(() => {
    if (!view || view === 'ALL') {
      return rawConversions?.analyticsConversionsOverTime ?? [];
    }
    return rawOverTime?.analyticsOverTime?.[view]?.conversions ?? [];
  }, [rawOverTime?.analyticsOverTime, rawConversions?.analyticsConversionsOverTime, view]);

  const chooseDateRange = useCallback(
    (value) => {
      if (value.special) {
        paramSet('date_range', value.special);
      } else if (value.start_date && value.end_date) {
        const newStartDate = getUnixTime(value.start_date);
        const newEndDate = getUnixTime(value.end_date);

        paramSet('date_range', `${newStartDate},${newEndDate}`);
      }
    },
    [paramSet],
  );

  const humanizedDateRange = humanizeDateRange(parsedDateRange);

  const hasData = Object.keys(webAnalyticsData || {}).length > 0;

  function downloadResults() {
    if (!canExportJson) return;

    if (!webAnalyticsData || Object.keys(webAnalyticsData).length === 0) {
      return;
    }

    try {
      const date = new Date().toISOString().slice(0, 10);

      const jsonString = JSON.stringify(webAnalyticsData, null, 2);
      const resultsBlob = new Blob([jsonString], { type: 'application/json' });
      downloadBlob(resultsBlob, `web-analytics-${date}.json`);
    } catch (error) {
      console.error('Failed to export analytics data:', error);
      notifications.error({ id: 'analyticsExportError', timeout: 3000, content: `Failed to export analytics data.` });
    }
  }

  const exportData = useCallback(async () => {
    if (!canExportCsv) return;

    const exportPath = location.pathname.replaceAll('-', '_');
    const ext = isDashboard ? 'xlsx' : 'csv';

    const searchParams = new URLSearchParams(location.search);

    const params = new URLSearchParams({
      site_id: String(selectedSite?.id ?? ''),
      token,
    });

    if (predefinedRange) {
      params.set('preset_date', predefinedRange);
    } else {
      params.set('start_at', toUtcStart(startDate));
      params.set('end_at', toUtcEnd(endDate));
    }

    if (searchParams.get('metric')) params.set('metric', searchParams.get('metric'));
    if (searchParams.get('order')) params.set('order', searchParams.get('order'));
    if (searchParams.get('sort')) params.set('sort', searchParams.get('sort'));
    if (searchParams.get('search')) params.set('search', searchParams.get('search'));

    let start, end;

    if (predefinedRange) {
      [start, end] = parsePredefinedRange(predefinedRange).map((v) => {
        return format(fromUnixTime(v), 'yyyy-MM-dd');
      });
    } else {
      [start, end] = [startDate, endDate].map((v) => {
        return format(fromUnixTime(v), 'yyyy-MM-dd');
      });
    }

    const datePart = start !== end ? `${start}-to-${end}` : start;

    const res = await fetch(`${window.CORE_API_URL}/export${exportPath}.${ext}?${params.toString()}`);
    const workbookBlob = await res.blob();

    const filename = isDashboard ? `web-analytics-${datePart}.xlsx` : `${view}-${datePart}.csv`;
    downloadBlob(workbookBlob, filename);
  }, [
    view,
    startDate,
    endDate,
    predefinedRange,
    location.pathname,
    location.search,
    selectedSite?.id,
    isDashboard,
    canExportCsv,
    token,
  ]);

  return (
    <>
      <div className="mx-auto flex w-full min-w-[940px] flex-col ">
        {view ? (
          <div>
            <Button
              variant="cancel"
              onClick={() => {
                setSearch('');
                setOrder(getDefaultOrder());
                setLastValidOrder(getDefaultOrder().id);
                navigate({
                  pathname: '/web-analytics',
                  search: ensureValidSearch(window.location.search, ['site', 'date_range', 'metric']),
                });
              }}
              className="!px-1 flex items-center space-x-1"
            >
              <ArrowIcon className="rotate-180 w-2.5 h-2.5 fill-current" />
              <span>Back</span>
            </Button>
          </div>
        ) : null}

        <div className="mb-2.5 flex justify-between items-center">
          <div className="ml-1 font-semibold text-lg text-black-pearl-500">{humanizedDateRange}</div>
          <div className="mr-1 flex items-center space-x-2">
            {canExport ? (
              <div className="flex justify-end w-full">
                <Popover className="relative" data-testid="question-menu">
                  {() => (
                    <>
                      <PopoverButton
                        as={Button}
                        className="text-cadet-blue-500 hover:text-dodger-blue-500 flex items-center space-x-2"
                        variant="secondary"
                      >
                        <DownloadIcon className="h-4 w-4 fill-current" />
                        <span>Export</span>
                      </PopoverButton>
                      <StyledPopoverPanel align="center" className="!w-[235px]">
                        {({ close }) => (
                          <>
                            {canExportCsv ? (
                              <PopoverItem
                                onClick={() => {
                                  close();
                                  exportData();
                                }}
                                className="group flex items-center"
                              >
                                {isDashboard ? `Export to spreadsheet (.xlsx)` : `Export to file (.csv)`}
                              </PopoverItem>
                            ) : null}
                            {canExportJson ? (
                              <PopoverItem
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  close();
                                  downloadResults();
                                }}
                                className="group flex items-center space-x-2"
                              >
                                <span>Export as JSON</span>
                              </PopoverItem>
                            ) : null}
                          </>
                        )}
                      </StyledPopoverPanel>
                    </>
                  )}
                </Popover>
              </div>
            ) : null}
            <DateRangePicker
              lowerBoundary={pickerLowerBoundary}
              upperBoundary={pickerUpperBoundary}
              startDate={startDate ? startDate * 1000 : undefined}
              endDate={endDate ? endDate * 1000 : undefined}
              special={predefinedRange}
              setDateRange={chooseDateRange}
              setCommonDateRange={chooseDateRange}
              showCommonRanges={true}
              size="lg"
              maxRange={365}
              timezone={TIMEZONES.UTC}
            />
          </div>
        </div>

        {hasData || view || isLoading ? (
          view ? (
            <DetailView
              isLoading={isLoading}
              analytics={webAnalyticsData}
              overTimeData={overTimeData}
              conversionsData={conversionsData}
              overTimeLoading={overTimeLoading}
              selectedSite={selectedSite}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
            />
          ) : (
            <ListView
              lowerBoundary={pickerLowerBoundary}
              upperBoundary={pickerUpperBoundary}
              isLoading={isLoading}
              analytics={webAnalyticsData}
              conversions={conversionsData}
            />
          )
        ) : (
          <NoMatchesWall
            text={
              <span>
                data with the selected date range.
                <br />
                Try changing the date range
              </span>
            }
          />
        )}

        {!view && hasData ? (
          <Button
            variant="cancel"
            className="justify-end w-full space-x-1.5 items-center mt-3.75 flex"
            onClick={() => scrollToLater(0)}
          >
            <ArrowIcon className="-rotate-90 fill-current w-2.5 h-2.5" />
            <span>Back to top</span>
          </Button>
        ) : null}
      </div>
    </>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
